import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { getPaymentVouchers } from '../../services/PaymentVoucherServices';
import { displayError } from '../../helpers';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';


function PaymentVoucherTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const [rangeFrom, rangeTo] = activeDateRange;

        if (!plantId || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }

        setIsLoading(true);
        return getPaymentVouchers(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
        })
            .then((response) => {
                setData({
                    records: (response.payment_voucher_list || []).map((row) => ({
                        ...row,
                        status_label: row.status ? 'Active' : 'Cancelled',
                    })),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load payment vouchers.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, salesSettings]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'payment_no', headerName: 'Payment No' },
        { fieldName: 'payment_date', headerName: 'Payment Date' },
        { fieldName: 'payment_type_label', headerName: 'Type' },
        { fieldName: 'party_name', headerName: 'Party' },
        { fieldName: 'bank_name', headerName: 'Bank' },
        { fieldName: 'payment_mode', headerName: 'Mode' },
        { fieldName: 'payment_against', headerName: 'Against' },
        { fieldName: 'payment_amount', headerName: 'Amount' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return data.records;
        }
        const terms = query.split(/\s+/).filter(Boolean);
        return data.records.filter((row) => {
            const haystack = Object.values(row)
                .filter((value) => value != null && value !== '')
                .map((value) => String(value).toLowerCase())
                .join(' ');
            return terms.every((term) => haystack.includes(term));
        });
    }, [data.records, searchText]);

    const summaryStats = useMemo(() => {
        const activeCount = filteredRecords.filter((row) => row.status).length;
        const totalAmount = filteredRecords.reduce((sum, row) => sum + (parseFloat(row.payment_amount) || 0), 0);
        return [
            { label: 'ACTIVE', value: String(activeCount), accent: 'b2b' },
            { label: 'TOTAL AMOUNT', value: totalAmount.toFixed(2), accent: 'b2c' },
        ];
    }, [filteredRecords]);

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, []),
        [salesSettings, plants],
    );

    const onEdit = (recordId) => {
        navigate(`/PaymentVoucherEdit/${recordId}`);
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
            filteredRecords,
            'PaymentVoucher',
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Payment Voucher',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            filteredRecords,
            'PaymentVoucher.pdf',
        );
    };

    const handleSettingsSave = (nextSettings) => {
        saveSalesListSettings(cookies.myUserId, nextSettings);
        setSalesSettings(nextSettings);
        setShowSettings(false);
        Swal.fire('Updated!', '', 'success');
    };

    useEffect(() => {
        getUserAllowedPlants(cookies)
            .then((plantList) => {
                setPlants((plantList.plant_list || [])
                    .filter((plant) => plant.status)
                    .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
                setIsInitialized(true);
            })
            .catch(() => setIsLoading(false));
    }, [cookies]);

    useEffect(() => {
        if (!isInitialized || plants.length === 0) {
            return;
        }
        fetchRecords();
    }, [isInitialized, plants.length, salesSettings, activeDateRange, fetchRecords]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="ACCOUNTS"
                    title="List of Payment Voucher"
                    subtitle="Vendor, customer refund, and expense payments."
                    modeLabel="VIEW MODE"
                    recordCount={filteredRecords.length}
                    recordLabel="payment vouchers"
                    summaryStats={summaryStats}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                    formLink="/PaymentVoucher"
                    addNewLabel="Add New"
                >
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title="List of Payment Voucher"
                                columns={columns}
                                data={filteredRecords}
                                onRowEdit={onEdit}
                                hideDelete
                                hidePrint
                                onCSVExport={handleCSVExport}
                                onPDFExport={handlePDFExport}
                                enableTableSearch={false}
                                isLoading={isLoading}
                                autoPinActionColumn={false}
                                compactRows
                            />
                        </div>
                    </div>
                </ListPageLayout>

                <SalesListSettingsModal
                    show={showSettings}
                    allowedPlants={plants}
                    customers={[]}
                    initialSettings={salesSettings}
                    onSave={handleSettingsSave}
                    onClose={() => setShowSettings(false)}
                />
            </div>
        </div>
    );
}

export default PaymentVoucherTable;
